import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Filter, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trNormalize } from "@/lib/chem";
import { HIGHER, LEVELS, useThemes } from "@/components/teacher/Social";
import { BADGES, itemBadges } from "@/components/teacher/ContentBadges";

export const EMPTY_FILTER = { q: "", scope: "all", owner: "all", theme: "all", outcome: "all", grade: "all", badge: "all" };
const SCOPES = [["all", "Tümü"], ["mine", "Benim oluşturduklarım"], ["shared", "Benimle paylaşılanlar"], ["others", "Diğer öğretmenlerin"], ["private", "Özel olanlar"]];

const SCOPE_TEST = {
  all: () => true, mine: (i) => i.owned, shared: (i) => i.shared && !i.owned,
  others: (i) => !i.owned, private: (i) => i.visibility === "private",
};

export const useFiltered = (items, f, textOf) => useMemo(() => {
  const q = trNormalize(f.q);
  return items.filter((i) => SCOPE_TEST[f.scope](i) && (f.owner === "all" || i.owner_name === f.owner)
    && (f.theme === "all" || i.theme_id === f.theme) && (f.outcome === "all" || i.outcome_ids?.includes(f.outcome))
    && (f.grade === "all" || i.grade === f.grade) && (!f.badge || f.badge === "all" || itemBadges(i).includes(f.badge))
    && (!q || trNormalize(`${textOf(i)} ${i.owner_name || ""} ${i.university || ""} ${i.faculty || ""} ${i.department || ""}`).includes(q)));
}, [items, f, textOf]);

export const usePaged = (list, size = 6) => {
  const [page, setPage] = useState(0);
  const pages = Math.max(1, Math.ceil(list.length / size));
  useEffect(() => { setPage(0); }, [list.length]);
  const p = Math.min(page, pages - 1);
  return { items: list.slice(p * size, (p + 1) * size), page: p, pages, setPage, total: list.length };
};

export const Pager = ({ pg, testId = "pager" }) => {
  if (pg.pages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-2 pt-1" data-testid={testId}>
      <Button size="sm" variant="outline" disabled={pg.page === 0} onClick={() => pg.setPage(pg.page - 1)} data-testid={`${testId}-prev`}><ChevronLeft className="h-4 w-4" /></Button>
      {Array.from({ length: pg.pages }, (_, i) => (
        <Button key={i} size="sm" variant={i === pg.page ? "default" : "ghost"} className="w-9" onClick={() => pg.setPage(i)} data-testid={`${testId}-page-${i + 1}`}>{i + 1}</Button>
      ))}
      <Button size="sm" variant="outline" disabled={pg.page === pg.pages - 1} onClick={() => pg.setPage(pg.page + 1)} data-testid={`${testId}-next`}><ChevronRight className="h-4 w-4" /></Button>
    </div>
  );
};

const Pick = ({ label, value, onChange, options, testId }) => (
  <div className="space-y-1">
    <p className="text-xs text-slate-400">{label}</p>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-10" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

export const ListFilter = ({ items, value, onChange, placeholder }) => {
  const owners = [...new Set(items.map((i) => i.owner_name).filter(Boolean))].sort();
  const themes = useThemes();
  const theme = themes.find((t) => t.id === value.theme);
  const set = (p) => onChange({ ...value, ...p });
  const active = ["scope", "owner", "theme", "outcome", "grade", "badge"].filter((k) => value[k] && value[k] !== "all").length;
  const school = value.grade !== "all" && !HIGHER.includes(value.grade);
  return (
    <div className="flex gap-3 items-center" data-testid="list-filter">
      <div className="relative flex-1 max-w-md">
        <Search className="absolute z-10 left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={value.q} onChange={(e) => set({ q: e.target.value })} placeholder={placeholder} className="pl-9 h-11" data-testid="filter-search-input" />
      </div>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="h-11 relative" data-testid="filter-button">
            <Filter className="h-4 w-4 mr-2" />Filtre
            {active > 0 && <span className="ml-2 h-5 min-w-5 px-1 rounded-full bg-[#FACC15] text-[#1A1405] text-xs font-bold grid place-items-center" data-testid="filter-count">{active}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 space-y-3" data-testid="filter-panel">
          <Pick label="Kapsam" value={value.scope} onChange={(v) => set({ scope: v })} options={SCOPES} testId="filter-scope" />
          <Pick label="Öğretmen" value={value.owner} onChange={(v) => set({ owner: v })} options={[["all", "Tüm öğretmenler"], ...owners.map((o) => [o, o])]} testId="filter-owner" />
          <Pick label="Rozet" value={value.badge || "all"} onChange={(v) => set({ badge: v })} options={[["all", "Tüm rozetler"], ...Object.entries(BADGES).map(([k, b]) => [k, b.label])]} testId="filter-badge" />
          <Pick label="Öğrenim düzeyi" value={value.grade} onChange={(v) => set({ grade: v, ...(HIGHER.includes(v) ? { theme: "all", outcome: "all" } : {}) })}
            options={[["all", "Tüm öğrenim düzeyleri"], ...LEVELS.map((g) => [g, g])]} testId="filter-grade" />
          {(value.grade === "all" || school) && (
            <Pick label="Tema" value={value.theme} onChange={(v) => set({ theme: v, outcome: "all" })} options={[["all", "Tüm temalar"], ...themes.map((t) => [t.id, t.name])]} testId="filter-theme" />
          )}
          {theme && theme.outcomes.length > 0 && (
            <Pick label="Öğrenme çıktısı" value={value.outcome} onChange={(v) => set({ outcome: v })} options={[["all", "Tüm öğrenme çıktıları"], ...theme.outcomes.map((o) => [o.id, o.text])]} testId="filter-outcome" />
          )}
          {active > 0 && <Button variant="ghost" size="sm" onClick={() => onChange({ ...EMPTY_FILTER, q: value.q })} data-testid="filter-clear"><X className="h-4 w-4 mr-1" />Filtreleri temizle</Button>}
        </PopoverContent>
      </Popover>
    </div>
  );
};
